import type {
  CalculateFareResponse,
  Invoice,
  Otherpromo,
  PaymentOption,
  v1RequestRide,
  VehicleWithPrice,
} from "@be-miniapp/api"
import { PaymentOptionKey } from "@be-miniapp/types"

import type {
  RideBookingInput,
  RideBookingNavigationPayload,
} from "../stores/ride-contracts"
import type { RideLocation } from "../stores/ride-data"

export type RideBookingPayload = Parameters<typeof v1RequestRide>[0]

export function createRideBookingPayload(
  params: RideBookingInput
): RideBookingPayload {
  const {
    vehicleType,
    isLongTrip,
    currentLocation,
    pickup,
    dropoff,
    priceOfCurrentVehicle,
    invoice,
    note,
    currentPromo,
    paymentMethod,
  } = params

  return {
    vehicle_type: vehicleType,
    is_befar: isLongTrip ? 1 : 0,
    latitude: pickup?.location?.latitude ?? 0,
    longitude: pickup?.location?.longitude ?? 0,
    current_latitude:
      currentLocation?.latitude ?? pickup?.location?.latitude ?? 0,
    current_longitude:
      currentLocation?.longitude ?? pickup?.location?.longitude ?? 0,
    customer_fare: priceOfCurrentVehicle?.fare?.fare ?? 2000,
    customer_fare_factor:
      priceOfCurrentVehicle?.fare?.customer_fare_factor ?? 1,
    driver_fare_factor: priceOfCurrentVehicle?.fare?.driver_fare_factor ?? 1,
    drop_location_address: dropoff?.address || "",
    drop_location_address_name: dropoff?.name || "",
    drop_location_address_label: dropoff?.label,
    duplicate_flag: 0,
    fare: priceOfCurrentVehicle?.fare?.fare ?? 2000,
    is_new_fare: 1,
    is_self_payment: 0,
    location_accuracy: 40,
    map_metadata: {
      pick_up: pickup?.metadata || "suggestion",
      pick_up_place_id: pickup?.placeId,
      drop_off: dropoff?.metadata || "suggestion",
      drop_off_place_id: dropoff?.placeId,
    },
    op_drop_latitude: dropoff?.location?.latitude ?? 0,
    op_drop_longitude: dropoff?.location?.longitude ?? 0,
    op_drop_place_id: dropoff?.placeId,
    pickup_location_address: pickup?.address || "",
    pickup_location_address_name: pickup?.name || "",
    request_location_address_label: pickup?.label,
    reverse_bid: 0,
    invoice_id: invoice?.id,
    customer_note: note,
    promo_to_apply: currentPromo?.type === 1 ? currentPromo?.id : undefined,
    coupon_id: currentPromo?.type === 2 ? currentPromo?.id : undefined,
    master_coupon: 0,
    business_id:
      paymentMethod?.payment_method === PaymentOptionKey.COOPERATE
        ? paymentMethod?.business_id?.toString()
        : undefined,
    card_id:
      paymentMethod?.payment_method === PaymentOptionKey.PAY_BY_CARD ||
      paymentMethod?.payment_method === PaymentOptionKey.MPGS
        ? paymentMethod?.cards_data?.id
        : undefined,
    pickup_saved_address_id:
      pickup?.id && pickup.id !== "0" ? pickup.id : undefined,
    dropoff_saved_address_id:
      dropoff?.id && dropoff.id !== "0" ? dropoff.id : undefined,
    region_id: priceOfCurrentVehicle?.region_id ?? 0,
    preferred_payment_mode:
      paymentMethod?.payment_method ?? PaymentOptionKey.CASH,
  }
}

export function createRideBookingErrorLogData(params: {
  error: unknown
  vehicleType: number
  cityId?: number
  paymentMethod?: PaymentOption
  currentPromo?: Otherpromo
  pickup?: RideLocation
  dropoff?: RideLocation
  isLongTrip?: boolean
  paymentOption?: PaymentOption
  defaultPaymentMethod: string
}) {
  const {
    error,
    vehicleType,
    cityId,
    paymentMethod,
    currentPromo,
    pickup,
    dropoff,
    isLongTrip,
    paymentOption,
    defaultPaymentMethod,
  } = params

  return {
    error: error,
    last_selected_vehicle_type: vehicleType || 0,
    city: cityId ?? 189,
    business_id:
      paymentMethod?.payment_method === PaymentOptionKey.COOPERATE
        ? paymentMethod?.business_id
        : undefined,
    card_id:
      paymentMethod?.payment_method === PaymentOptionKey.PAY_BY_CARD ||
      paymentMethod?.payment_method === PaymentOptionKey.MPGS
        ? paymentMethod?.cards_data?.id
        : undefined,
    coupon_id: currentPromo?.type === 2 ? currentPromo?.id : undefined,
    promo_id: currentPromo?.type === 1 ? currentPromo?.id : undefined,
    expected_highlight_vehicles: [vehicleType],
    best_coupon_disabled: false,
    preferred_payment_mode:
      paymentOption?.payment_method || defaultPaymentMethod,
    pick_up: {
      pickup_location_address: pickup?.address || "",
      pickup_location_address_name: pickup?.name || "",
      latitude: pickup?.location?.latitude ?? 0,
      longitude: pickup?.location?.longitude ?? 0,
    },
    drop_off: {
      drop_location_address: dropoff?.address || "",
      drop_location_address_name: dropoff?.name || "",
      latitude: dropoff?.location?.latitude ?? 0,
      longitude: dropoff?.location?.longitude ?? 0,
    },
    booking_service_type: 1,
    is_befar: isLongTrip ? 1 : 0,
  }
}

export function shouldPromptPromoNotUsable(params: {
  canUseCurrentPromo: boolean
  currentPromo: Otherpromo | undefined
}) {
  return !!params.currentPromo && !params.canUseCurrentPromo
}

export function resolveRideBookingInvoice(params: {
  invoice: Invoice | undefined
  autoCreateInvoice: boolean
  preloadedInvoice: Invoice | undefined
}) {
  const { invoice, autoCreateInvoice, preloadedInvoice } = params
  if (!autoCreateInvoice) return invoice
  return preloadedInvoice ?? invoice
}

export function pickVehicleDataForBookingNavigation(params: {
  fare: CalculateFareResponse | undefined
  vehicleType: number
}): VehicleWithPrice | undefined {
  const { fare, vehicleType } = params
  return fare?.prices?.find((price) => price.vehicle_type === vehicleType)
}

export function buildRideBookingNavigationPayload(params: {
  sessionId: number
  fare: CalculateFareResponse | undefined
  vehicleType: number
}): RideBookingNavigationPayload {
  const { sessionId, fare, vehicleType } = params
  return {
    sessionId,
    vehicleData: pickVehicleDataForBookingNavigation({ fare, vehicleType }),
  }
}
